import { Inject } from '@odinjs/odin';

import { CallsData, Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { HPMSOnClickTermUse } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FormAPI, PanelAPI, TextAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnChange, OnClose, OnMount } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { NewCallAliases as Aliases, FragmentNames } from '../../codegen';
import { Route } from '../../route';
import BaseController from '../BaseController';

import { Call, CallCategory } from './calls-controller';

interface CallType {
  dsCallType: string,
  nrSequenceCallType: number,
  required: string,
  nrSeqResponsabilityTerm: number
}

@Controller({ fragment: FragmentNames.NEW_CALL })
class NewCallController extends BaseController {

  @Inject(Aliases.FORM_NEW_CALL)
  private formNewCall: FormAPI;
  @Inject(Aliases.FIELD_REQUIRED_ATTACHMENT)
  private fieldRequiredAttachment: FieldAPI;
  @Inject(Aliases.FETCH_GUIDANCE)
  private fetchGuidance: FetchAPI;
  @Inject(Aliases.TEXT_GUIDANCE)
  private textGuidance: TextAPI;
  @Inject(Aliases.FIELD_RESPONSABILITY_TERM)
  private fieldResponsabilityTerm: FieldAPI;
  @Inject(Aliases.PANEL_RESPONSABILITY_TERM)
  private panelResponsabilityTerm: PanelAPI;
  @Inject(Aliases.FETCH_TERM)
  private fetchTerm: FetchAPI;

  @OnMount(Aliases.PANEL_NEW_CALL)
  onMountPanel(): void {
    const { nrSeqCategory } = this.historyService.getCurrentRouteParams() as unknown as CallCategory;
    this.context.set({ nrSeqCategory });
  }

  @OnChange(Aliases.AUTOCOMPLETE_TYPE)
  onChangeCallType(event: ChangeEvent<CallType>): void {
    const { nrSeqCategory } = this.historyService.getCurrentRouteParams();
    const { required, nrSequenceCallType, nrSeqResponsabilityTerm } = event.value;
    if (nrSeqResponsabilityTerm) {
      this.fieldResponsabilityTerm.setVisible(true);
      this.fieldResponsabilityTerm.setValidators([{ name: 'required', options: {} }] as Validator[]);
    } else {
      this.fieldResponsabilityTerm.setVisible(false);
      this.fieldResponsabilityTerm.setValidators([{}] as Validator[]);
    }
    this.context.set({ nrSeqResponsabilityTerm });
    this.fetchGuidance.setParameters({ nrSeqCategory, nrSequenceCallType });
    this.fetchGuidance.forceFetch();
    if (required && required === 'S') {
      this.fieldRequiredAttachment.setVisible(true);
    } else {
      this.fieldRequiredAttachment.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_GUIDANCE)
  onAfterFetchGuidance(event: FetchEvent): void {
    const data = event.data as CallsData;
    if (isEmptyArrayOrObject(data.guidance)) {
      this.textGuidance.setValue('');
    }
  }

  @OnBeforeSubmit(Aliases.FORM_NEW_CALL)
  OnBeforeSubmit(event: SubmitEvent): void {
    const record = event.record;
    const { nrSeqProvider } = record;
    const { nrSeqCategory } = this.context.getBulkData() as unknown as CallCategory;
    const callCategory = { nrSeqCategory };
    const body = { ...record, callCategory, nrSeqProvider: nrSeqProvider.nrSeqProvider };
    this.formNewCall.setLoading(true);
    HttpService.post<Call>(Endpoint.POST.NEW_CALL, {}, { body })
      .then((response) => {
        if (!response.body) return;
        const { nrSeqCall } = response.body;
        this.formNewCall.setEditing(false);
        this.fieldRequiredAttachment.setVisible(false);
        this.formNewCall.setLoading(false);
        this.historyService.push(Route.CALL_DETAIL, { nrSeqCall });
        ToastService.open({ message: 'provider.request-sent', level: ToastLevel.SUCCESS });
      })
      .catch((response) => {
        this.formNewCall.setLoading(false);
        this.formNewCall.setEditing(true);
        ToastService.open(response);
      });
  }

  @OnCancel(Aliases.FORM_NEW_CALL)
  oncancel(): void {
    this.historyService.push(Route.CALLS);
  }

  @HPMSOnClickTermUse(Aliases.HPMS_TERM_RESPONSABILITY_CHECKBOX)
  onClickResponsabilityTerm(): void {
    const { nrSeqResponsabilityTerm } = this.context.getBulkData();
    this.panelResponsabilityTerm.setVisible(true);
    this.fetchTerm.setParameters({ nrSeqResponsabilityTerm });
    this.fetchTerm.forceFetch();
  }

  @OnClose(Aliases.MODAL_RESPONSABILITY_TERM)
  onCloseModalResponsabilityTerm(): void {
    this.panelResponsabilityTerm.setVisible(false);
  }
}

export {
  NewCallController,
};
